<script setup lang="ts">
import type { ChampionshipCategory } from "@/model/championship/championship-category"
import { computed } from "vue"
import { useI18n } from "vue-i18n"
import type { BlankRegistration } from "../championship-registration"

const { t } = useI18n()

const props = defineProps<{
    category: ChampionshipCategory
    modelValue: BlankRegistration
    /** Disable submit button? */
    submitting?: boolean
}>()

const emit = defineEmits<{
    (e: "save"): void
    (e: "update:modelValue", value: BlankRegistration): void
}>()

/*─────────────────────────────────────┐
│  blank registration                  │
└─────────────────────────────────────*/
const blankRegistration = computed({
    get: () => props.modelValue,
    set: (value: BlankRegistration) => {
        emit("update:modelValue", value)
    },
})

const blankSubmitAllowed = computed(() => {
    if (!blankRegistration.value.numberOfParticipants) {
        // at least 1 starter is required
        return false
    }

    if (
        blankRegistration.value.numberOfEntourages === null ||
        blankRegistration.value.numberOfAlternates === null ||
        blankRegistration.value.numberOfParticipants === null
    ) {
        return false
    }

    if (participantsError.value || alternatesError.value || entouragesError.value) {
        return false
    }

    return true
})

async function submit() {
    emit("save")
}

/**
 * Prevent non-integer characters in number inputs.
 * Allows digits and common control/navigation keys.
 */
function onIntegerKeydown(e: KeyboardEvent) {
    // Allow: Ctrl/Cmd shortcuts (copy, paste, select all, etc.)
    if (e.ctrlKey || e.metaKey) {
        return
    }

    // Allow navigation and control keys
    const allowedKeys = [
        "Backspace",
        "Delete",
        "Tab",
        "Enter",
        "Escape",
        "ArrowLeft",
        "ArrowRight",
        "ArrowUp",
        "ArrowDown",
        "Home",
        "End",
    ]
    if (allowedKeys.includes(e.key)) {
        return
    }

    // Allow digits 0-9 only
    if (/^[0-9]$/.test(e.key)) {
        return
    }

    // Block everything else (e, E, +, -, ., , and any other non-digit)
    e.preventDefault()
}

// async function registerBlank() {
//     if (!blankSubmitAllowed.value) {
//         return
//     }
//
//     registrationSubmitting.value = true
//     await http.post(
//         `/championship/registration/${props.data.registrationId}/select-team/register-blank`,
//         blankRegistration,
//     )
//     registrationSubmitting.value = false
//     emit("updated")
// }

/*─────────────────────────────────────┐
│  validation                          │
└─────────────────────────────────────*/
const participantsError = computed(() => {
    if (!props.category || blankRegistration.value.numberOfParticipants === null) {
        return null
    }

    if ((blankRegistration.value.numberOfParticipants ?? 0) < (props.category.criteriaMinStarters ?? 0)) {
        return t("@tasks:tasks.championship_registration.wizard.not_enough_participants", {
            min: props.category.criteriaMinStarters,
        })
    }

    if ((blankRegistration.value.numberOfParticipants ?? 0) > (props.category.criteriaMaxStarters ?? 0)) {
        return t("@tasks:tasks.championship_registration.wizard.too_many_participants", {
            max: props.category.criteriaMaxStarters,
        })
    }

    return null
})
const alternatesError = computed(() => {
    if (!props.category) {
        return null
    }

    if ((blankRegistration.value.numberOfAlternates ?? 0) > (props.category.criteriaMaxReplacements ?? 0)) {
        return t("@tasks:tasks.championship_registration.wizard.too_many_alternates", {
            max: props.category.criteriaMaxReplacements,
        })
    }

    return null
})
const entouragesError = computed(() => {
    if (!props.category) {
        return null
    }

    const numberOfEntourages = blankRegistration.value.numberOfEntourages ?? 0

    if (numberOfEntourages > (props.category.criteriaMaxEntourages ?? 0)) {
        return t("@tasks:tasks.championship_registration.wizard.too_many_entourages", {
            max: props.category.criteriaMaxEntourages,
        })
    }

    return null
})
</script>
<template>
    <label for="numberOfParticipants">
        {{ $t("@tasks:tasks.championship_registration.wizard.number_of_participants") }}
    </label>
    <input
        type="number"
        id="numberOfParticipants"
        class="form-control"
        v-model.number="blankRegistration.numberOfParticipants"
        min="0"
        step="1"
        inputmode="numeric"
        pattern="[0-9]*"
        @keydown="onIntegerKeydown"
    />
    <div class="text-danger">
        {{ participantsError }}
    </div>

    <label for="numberOfAlternates" class="mt-3">
        {{ $t("@tasks:tasks.championship_registration.wizard.number_of_alternates") }}
    </label>
    <input
        type="number"
        id="numberOfAlternates"
        class="form-control"
        v-model.number="blankRegistration.numberOfAlternates"
        min="0"
        step="1"
        inputmode="numeric"
        pattern="[0-9]*"
        @keydown="onIntegerKeydown"
    />
    <div class="text-danger">
        {{ alternatesError }}
    </div>

    <label for="numberOfEntourages" class="mt-3">
        {{ $t("@tasks:tasks.championship_registration.wizard.number_of_entourages") }}
    </label>
    <input
        type="number"
        id="numberOfEntourages"
        class="form-control"
        v-model.number="blankRegistration.numberOfEntourages"
        min="0"
        step="1"
        inputmode="numeric"
        pattern="[0-9]*"
        @keydown="onIntegerKeydown"
    />

    <div class="text-danger">
        {{ entouragesError }}
    </div>

    <div class="d-flex justify-content-end mt-4">
        <button class="btn btn-primary mb-5 mt-3" @click="submit()" :disabled="!blankSubmitAllowed || submitting">
            {{ $t("@tasks:tasks.championship_registration.wizard.register_blank_submit") }}
        </button>
    </div>
</template>
